import isEqual from 'lodash/isEqual'

import { type K8sAutocompleteSearchOption } from 'components/services/create/form-controls/fingerprint/kubernetes/types'
import { DynatraceEntitySelectionSource } from 'components/status-checks/form/status-check/dynatrace/DynatraceMonitorSelection'

import type {
  DynatraceEntitySelectionAsEntityId,
  DynatraceEntitySelectionAsK8sTargeting,
  DynatraceEntitySelectionAsProblemsUrl,
  DynatraceEntitySelector
} from 'components/status-checks/status-checks-form-utils'
import type {
  DynatraceEntityId,
  DynatraceK8sQuery
} from 'queries/status-checks/pre-flight/types'

/*
 * Type Guards to check the shape of requestParams for Dynatrace entity selection
 */
function areRequestParamsDynatraceEntityId(
  requestParams?: Record<string, unknown>
): requestParams is { entityId: DynatraceEntityId } {
  return (
    requestParams !== undefined &&
    'entityId' in requestParams &&
    typeof requestParams.entityId === 'string'
  )
}

function areRequestParamsDynatraceK8sQuery(
  requestParams?: Record<string, unknown>
): requestParams is DynatraceK8sQuery {
  if (!requestParams) return false

  return (
    'clusterId' in requestParams &&
    'kind' in requestParams &&
    'name' in requestParams &&
    'namespace' in requestParams &&
    typeof requestParams.clusterId === 'string' &&
    typeof requestParams.kind === 'string' &&
    typeof requestParams.name === 'string' &&
    typeof requestParams.namespace === 'string'
  )
}

/*
 * Type Guards to check the dynatrace selector type to narrow the shape of the selector
 */
export function isDynatraceSelectorEntityIdType(
  dynatraceSelector?: DynatraceEntitySelector
): dynatraceSelector is DynatraceEntitySelectionAsEntityId {
  return dynatraceSelector?.type === DynatraceEntitySelectionSource.ENTITY_ID
}

export function isDynatraceSelectorK8sQueryType(
  dynatraceSelector?: DynatraceEntitySelector
): dynatraceSelector is DynatraceEntitySelectionAsK8sTargeting {
  return (
    dynatraceSelector?.type === DynatraceEntitySelectionSource.K8S_TARGETING
  )
}

export function isDynatraceSelectorProblemsUrlType(
  dynatraceSelector?: DynatraceEntitySelector
): dynatraceSelector is DynatraceEntitySelectionAsProblemsUrl {
  return dynatraceSelector?.type === DynatraceEntitySelectionSource.PROBLEMS_URL
}

/**
 * Constructs a `DynatraceK8sQuery` object from a given `K8sAutocompleteSearchOption` object.
 */
export function getK8sQueryFromK8sAutocompleteSearchOption({
  params,
  tags
}: K8sAutocompleteSearchOption): DynatraceK8sQuery {
  const { kind, name } = params
  const { cluster: clusterId, namespace } = tags

  return { clusterId, kind, name, namespace }
}

/**
 * Matches a given name to a supplied list of `K8sAutocompleteSearchOption` items
 * to extract a selected option object, or returns null if no match is found.
 */
export function findK8sAutocompleteSearchOptionFromK8sQuery(
  options: K8sAutocompleteSearchOption[],
  k8sQuery: DynatraceK8sQuery | undefined
) {
  if (!k8sQuery) return

  return options.find((option) =>
    isEqual(getK8sQueryFromK8sAutocompleteSearchOption(option), k8sQuery)
  )
}

/**
 * Hydrates the proper 'dynatraceSelector' field based on requestParams
 * received from the status check object.
 */
export function getDynatraceSelectorFromRequestParams(
  requestParams: Record<string, unknown> | undefined
): DynatraceEntitySelector {
  if (areRequestParamsDynatraceEntityId(requestParams)) {
    return {
      type: DynatraceEntitySelectionSource.ENTITY_ID,
      entityId: requestParams.entityId
    }
  }

  if (areRequestParamsDynatraceK8sQuery(requestParams)) {
    return {
      type: DynatraceEntitySelectionSource.K8S_TARGETING,
      k8sQuery: requestParams
    }
  }

  return {
    type: DynatraceEntitySelectionSource.PROBLEMS_URL
  }
}
